import React, { ReactNode } from 'react';
import { Row, Col } from 'antd';
import * as Yup from 'yup';
import { Select } from 'formik-antd';

import Input from 'components/formik/Input';
import FormItem from 'components/formik/FormItem';
import ContactForSelect from 'components/formik/ContactForSelect';
import { isComponentsEqual } from 'utils/reactUtils';
import {
  PreferredContactMethodEnum,
} from 'api/backofficeApiClient';

import { getFieldLabel } from 'utils/formUtils';
import { getFormItemDataId, getFormItemName } from 'utils/fieldArrayUtils';

import style from './ContactsSection.module.less';

export const contactsValidationSchema = {
  firstName: Yup.string()
    .trim()
    .max(120, 'First name should be 120 characters maximum'),
  lastName: Yup.string()
    .trim()
    .max(120, 'Last name should be 120 characters maximum'),
  title: Yup.string().trim().max(120, 'Title should be 120 characters maximum'),
  email: Yup.string()
    .email('Email format is invalid')
    .max(120, 'Email should be 120 characters maximum'),
  phone: Yup.string()
    .trim()
    .max(120, 'Phone number should be 120 characters maximum'),
  preferredContactMethod: Yup.array().of(Yup.number()),
  contactFor: Yup.array().of(Yup.number()).nullable(),
};

const preferredContactMethodOptions = [
  {
    label: 'Phone',
    value: PreferredContactMethodEnum.Phone,
  },
  {
    label: 'Email',
    value: PreferredContactMethodEnum.Email,
  },
  {
    label: 'Text',
    value: PreferredContactMethodEnum.Text,
  },
  {
    label: 'Teams',
    value: PreferredContactMethodEnum.Teams,
  },
];

type Props = {
  title?: string;
  index?: number;
  formNamePrefix?: string;
  actionButtons?: ReactNode;
  isPhoneRequired?: boolean;
  isTitleEnabled?: boolean;
  disabled?: boolean;
  areFieldsRequired?: boolean;
  showPreferredContactMethod?: boolean;
  showContactFor?: boolean;
  preferredContactMethodRequired?: boolean;
  contactForRequired?: boolean;
};

export default React.memo(
  (props: Props) => {
    const {
      index,
      title,
      actionButtons = null,
      formNamePrefix,
      isPhoneRequired = true,
      isTitleEnabled = true,
      disabled = false,
      areFieldsRequired = true,
      showPreferredContactMethod = false,
      showContactFor = false,
      preferredContactMethodRequired = areFieldsRequired,
      contactForRequired = areFieldsRequired,
    } = props;

    return (
      <>
        {!!title && <div className="modal-title">{title}</div>}

        <Row gutter={40}>
          <Col xs={24} md={12}>
            <Row gutter={20}>
              <Col xs={24} md={12}>
                <FormItem
                  label={getFieldLabel('First name', areFieldsRequired)}
                  name={getFormItemName({
                    name: 'firstName',
                    index,
                    prefix: formNamePrefix,
                  })}
                  data-id={getFormItemDataId('contactFirstNameField', index)}
                >
                  <Input
                    disabled={disabled}
                    placeholder="Enter first name"
                    name={getFormItemName({
                      name: 'firstName',
                      index,
                      prefix: formNamePrefix,
                    })}
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  label={getFieldLabel('Last name', areFieldsRequired)}
                  name={getFormItemName({
                    name: 'lastName',
                    index,
                    prefix: formNamePrefix,
                  })}
                  data-id={getFormItemDataId('contactLastNameField', index)}
                >
                  <Input
                    disabled={disabled}
                    placeholder="Enter last name"
                    name={getFormItemName({
                      name: 'lastName',
                      index,
                      prefix: formNamePrefix,
                    })}
                  />
                </FormItem>
              </Col>
            </Row>
          </Col>

          <Col xs={24} md={12}>
            <Row gutter={20}>
              <Col xs={24} md={12}>
                <FormItem
                  label={getFieldLabel('Email', areFieldsRequired)}
                  name={getFormItemName({
                    name: 'email',
                    index,
                    prefix: formNamePrefix,
                  })}
                  data-id={getFormItemDataId('contactEmailField', index)}
                >
                  <Input
                    disabled={disabled}
                    placeholder="Enter email"
                    name={getFormItemName({
                      name: 'email',
                      index,
                      prefix: formNamePrefix,
                    })}
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  label={getFieldLabel('Phone number', isPhoneRequired)}
                  name={getFormItemName({
                    name: 'phone',
                    index,
                    prefix: formNamePrefix,
                  })}
                  data-id={getFormItemDataId('contactPhoneField', index)}
                >
                  <Input
                    disabled={disabled}
                    placeholder="Enter phone number"
                    name={getFormItemName({
                      name: 'phone',
                      index,
                      prefix: formNamePrefix,
                    })}
                  />
                </FormItem>
              </Col>
            </Row>
          </Col>
        </Row>

        {(showPreferredContactMethod || showContactFor) && (
          <Row gutter={40}>
            {showPreferredContactMethod && (
              <Col xs={24} md={12}>
                <FormItem
                  label={getFieldLabel(
                    'Preferred contact method',
                    preferredContactMethodRequired,
                  )}
                  name={getFormItemName({
                    name: 'preferredContactMethod',
                    index,
                    prefix: formNamePrefix,
                  })}
                  data-id={getFormItemDataId(
                    'contactPreferredContactMethodField',
                    index,
                  )}
                >
                  <Select
                    disabled={disabled}
                    mode="multiple"
                    allowClear
                    placeholder="Select preferred contact methods"
                    name={getFormItemName({
                      name: 'preferredContactMethod',
                      index,
                      prefix: formNamePrefix,
                    })}
                  >
                    {preferredContactMethodOptions.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>
            )}

            {showContactFor && (
              <Col xs={24} md={12}>
                <FormItem
                  label={getFieldLabel('Contact for', contactForRequired)}
                  name={getFormItemName({
                    name: 'contactFor',
                    index,
                    prefix: formNamePrefix,
                  })}
                  data-id={getFormItemDataId('contactForField', index)}
                >
                  <ContactForSelect
                    disabled={disabled}
                    fieldName={getFormItemName({
                      name: 'contactFor',
                      index,
                      prefix: formNamePrefix,
                    })}
                  />
                </FormItem>
              </Col>
            )}
          </Row>
        )}

        <Row gutter={40} justify="end" align="middle">
          {isTitleEnabled && (
            <Col xs={24} md={12}>
              <FormItem
                label={getFieldLabel('Title', areFieldsRequired)}
                name={getFormItemName({
                  name: 'title',
                  index,
                  prefix: formNamePrefix,
                })}
                data-id={getFormItemDataId('contactTitleField', index)}
              >
                <Input
                  disabled={disabled}
                  placeholder="Enter title"
                  name={getFormItemName({
                    name: 'title',
                    index,
                    prefix: formNamePrefix,
                  })}
                />
              </FormItem>
            </Col>
          )}

          <Col xs={24} md={12}>
            <div className={style.action}>{actionButtons && actionButtons}</div>
          </Col>
        </Row>
      </>
    );
  },
  (prevProps: Props, nextProps: Props) => {
    return (
      prevProps.index === nextProps.index &&
      isComponentsEqual(prevProps.actionButtons, nextProps.actionButtons)
    );
  },
);
